import { NSARouterMakerResult } from "namirasoft-account-react";
import { FilterItem, FilterItemOperator } from "namirasoft-core";
import { BranchRow, CurrencyRow, JournalEntryFullRow, JournalEntryInputRow, JournalEntryRow, NamirasoftERPMetaDatabase } from "namirasoft-erp";
import { NSBox, NSBoxBoolean, NSBoxDateTime, NSBoxEntity, NSBoxString, NSBoxTextArea, NSColumn, NSLine, NSRepeater, NSRow } from "namirasoft-site-react";
import { Component, createRef } from "react";
import { ErpEntities } from "../ErpEntities";
import { ErpLayout } from '../ErpLayout';
import { ErpRouterProps } from "../ErpRouterProps";
import { ErpScope, ErpScopeField, ErpScopeMode } from "../ErpScope";
import { JournalEntryLineInput, JournalEntryLineItem } from "../components/JournalEntryLineItem";
import { JournalEntryLinesView } from "../components/JournalEntryLinesView";

interface Props extends NSARouterMakerResult, ErpRouterProps { isEdit: boolean; }

interface State { totalDebit: number; totalCredit: number; scopeVersion: number; multiCurrency: boolean; entryCurrencyId: string | null; }

export default class JournalEntryEditPage extends Component<Props, State>
{
    override state: State = { totalDebit: 0, totalCredit: 0, scopeVersion: 0, multiCurrency: false, entryCurrencyId: null };

    private unsubscribeScope: (() => void) | null = null;

    Currency = createRef<NSBoxEntity<CurrencyRow>>();
    MultiCurrency = createRef<NSBoxBoolean>();
    DateTime = createRef<NSBoxDateTime>();
    ReferenceNumber = createRef<NSBoxString>();
    ReferenceDateTime = createRef<NSBoxDateTime>();
    Description = createRef<NSBoxTextArea>();
    Enabled = createRef<NSBoxBoolean>();
    Lines = createRef<NSRepeater<JournalEntryLineItem, JournalEntryLineInput>>();

    override componentDidMount(): void
    {
        if (!this.props.isEdit)
            this.loadCurrencyFromBranch();
        this.unsubscribeScope = ErpScope.subscribe(() =>
        {
            this.setState(s => ({ scopeVersion: s.scopeVersion + 1 }));
            if (!this.props.isEdit)
                this.loadCurrencyFromBranch();
        });
    }

    private loadCurrencyFromBranch = (): void =>
    {
        let branchId = ErpScope.getBranchIdRaw();
        if (branchId && !this.Currency.current?.getValueOne(false))
        {
            this.props.servers.erp.branch.Get(branchId)
                .then((branch: BranchRow) =>
                {
                    if (branch?.default_currency_id && !this.Currency.current?.getValueOne(false))
                    {
                        this.Currency.current?.setValueOne(branch.default_currency_id);
                        this.setState({ entryCurrencyId: branch.default_currency_id });
                    }
                })
                .catch(console.error);
        }
    };

    private onCurrencyChanged = (): void =>
    {
        let id = this.Currency.current?.getValueOne(false) ?? null;
        this.setState({ entryCurrencyId: id });
    };

    private onMultiCurrencyChanged = (): void =>
    {
        let v = this.MultiCurrency.current?.getValueBool(false) ?? false;
        this.setState({ multiCurrency: v });
    };

    override componentWillUnmount(): void
    {
        this.unsubscribeScope?.();
    }

    private recomputeTotals = (): void =>
    {
        let lines = this.Lines.current?.getValue(false) ?? [];
        let totalDebit = 0;
        let totalCredit = 0;
        for (let line of lines)
        {
            totalDebit += Number(line.debit) || 0;
            totalCredit += Number(line.credit) || 0;
        }
        this.setState({ totalDebit, totalCredit });
    };

    override render()
    {
        let db = new NamirasoftERPMetaDatabase(this.props.account.token_manager, console.error);
        let companyId = ErpScope.getCompanyIdRaw();
        let branchId = ErpScope.getBranchIdRaw();

        return (
            <ErpLayout<JournalEntryRow, JournalEntryInputRow>
                {...this.props}
                mode={ErpScopeMode.Select}
                fields={[ErpScopeField.Company, ErpScopeField.Branch, ErpScopeField.FiscalYear]}
                pages={{
                    entity: ErpEntities.journal_entry(this.props),
                    edit: {
                        ...this.props,
                        ui: {
                            tab_page_edit: {
                                ui: {
                                    extra_tabs: [
                                        {
                                            title: "Reference",
                                            clicked: false,
                                            getContent: () => (
                                                <NSColumn>
                                                    <NSRow style={{ gap: 8 }}>
                                                        <NSBoxDateTime ref={this.ReferenceDateTime} required={false} nullable nullableDefaultValue title="Reference Date Time" info={{ link: "https://namirasoft.com/erp/console-guide/journal-entry/#field-reference-date" }} style={{ width: NSBox.width.one }} />
                                                        <NSBoxString ref={this.ReferenceNumber} required={false} title="Reference Number" info={{ link: "https://namirasoft.com/erp/console-guide/journal-entry/#field-reference-number" }} style={{ width: NSBox.width.one }} />
                                                    </NSRow>
                                                </NSColumn>
                                            ),
                                        },
                                    ],
                                },
                                editor: {
                                    getInputRow: (): JournalEntryInputRow =>
                                    {
                                        let lines = this.Lines.current?.getValue() ?? [];
                                        let body: JournalEntryInputRow = {
                                            company_id: ErpScope.getCompanyId() ?? "",
                                            branch_id: ErpScope.getBranchId() ?? "",
                                            fiscal_year_id: ErpScope.getFiscalYearId() ?? "",
                                            date_time: new Date(this.DateTime.current?.getValue() ?? new Date().toISOString()),
                                            reference_number: this.ReferenceNumber.current?.getValue() ?? null,
                                            reference_date_time: this.ReferenceDateTime.current?.getValue()
                                                ? new Date(this.ReferenceDateTime.current!.getValue()!)
                                                : null,
                                            description: this.Description.current?.getValue() ?? null,
                                            enabled: this.Enabled.current?.getValueBool() ?? true,
                                            lines,
                                            journal_entry_category: [],
                                            journal_entry_tag: [],
                                            journal_entry_field: [],
                                        };
                                        return body;
                                    },
                                    setRow: (row: JournalEntryRow) =>
                                    {
                                        ErpScope.set({
                                            company_id: row.company_id,
                                            branch_id: row.branch_id,
                                            fiscal_year_id: (row as unknown as { fiscal_year_id: string | null }).fiscal_year_id ?? null,
                                        });
                                        this.DateTime.current?.setValue(new Date(row.date_time).toISOString());
                                        this.ReferenceNumber.current?.setValue(row.reference_number);
                                        this.ReferenceDateTime.current?.setValue(
                                            row.reference_date_time ? new Date(row.reference_date_time).toISOString() : null
                                        );
                                        this.Description.current?.setValue(row.description);
                                        this.Enabled.current?.setValueBool(row.enabled);

                                        let fullLines = (row as JournalEntryFullRow).lines ?? [];
                                        let lineCurrencyIds = fullLines.map((r) => r.currency_id).filter((c): c is string => !!c);
                                        let firstCurrencyId = lineCurrencyIds[0] ?? "";
                                        let isMulti = lineCurrencyIds.some((c) => c !== firstCurrencyId);
                                        let entryCurrencyId = isMulti ? "" : firstCurrencyId;

                                        this.Currency.current?.setValueOne(entryCurrencyId || null);
                                        this.MultiCurrency.current?.setValueBool(isMulti);
                                        this.setState({ multiCurrency: isMulti, entryCurrencyId: entryCurrencyId || null });

                                        let lines: JournalEntryLineInput[] = [...fullLines]
                                            .sort((a, b) => a.number - b.number)
                                            .map((r) => ({
                                                account_id: r.account_id,
                                                party_id: r.party_id,
                                                tag_ids: r.tag_ids ?? [],
                                                currency_id: r.currency_id ?? entryCurrencyId,
                                                debit: r.debit,
                                                credit: r.credit,
                                            }));
                                        lines.push({ account_id: "", party_id: null, tag_ids: [], currency_id: entryCurrencyId, debit: 0, credit: 0 });
                                        this.Lines.current?.setValue(lines, () => this.recomputeTotals());
                                    }
                                }
                            },
                            getChildrenAfter: (id, row) =>
                            {
                                if (!row || !id)
                                    return null;
                                let row_full = row as JournalEntryFullRow;
                                let firstCurrencyId = row_full.lines.find((r) => !!r.currency_id)?.currency_id ?? "";
                                let isMulti = row_full.lines.some((r) => r.currency_id && r.currency_id !== firstCurrencyId);
                                let entryCurrencyId = isMulti ? "" : firstCurrencyId;
                                return (
                                    <JournalEntryLinesView
                                        {...this.props}
                                        journal_entry_id={id}
                                        currency_id={entryCurrencyId}
                                        rows={row_full.lines}
                                    />
                                );
                            },
                        }
                    }
                }}
            >
                <NSColumn >
                    <NSRow style={{ justifyContent: "flex-start" }}>
                        <NSBoxEntity<CurrencyRow>
                            ref={this.Currency} title="Currency" required multiple={false}
                            table_name="currency" table={db.tables.currency}
                            list={(filters, page, size, sorts) =>
                            {
                                let f: FilterItem[] = filters ? [...filters] : [];
                                f.push(new FilterItem(db.tables.currency as any, db.tables.currency.columns.enabled, false, FilterItemOperator.all.equals, "1"));
                                return this.props.servers.erp.currency.List(f, page, size, sorts, null);
                            }}
                            getValue={(item) => item.id} getTitle={(item) => item.name} getSort={() => null}
                            style={{ width: NSBox.width.one, flexGrow: 0 }}
                            onChanged={this.onCurrencyChanged}
                        />
                        <NSBoxBoolean
                            ref={this.MultiCurrency}
                            required={true}
                            checked={false}
                            title="Multi Currency"
                            style={{ width: 175, flexGrow: 0 }}
                            onChanged={this.onMultiCurrencyChanged}
                        />
                        <NSBoxDateTime
                            ref={this.DateTime}
                            required
                            defaultValue={this.props.isEdit ? undefined : new Date()}
                            title="Date Time" info={{ link: "https://namirasoft.com/erp/console-guide/journal-entry/#field-date-time" }}
                            style={{ width: NSBox.width.one, flexGrow: 0, marginLeft: "auto" }}
                        />
                        <NSBoxBoolean
                            ref={this.Enabled}
                            required
                            checked={true}
                            title="Enabled" info={{ link: "https://namirasoft.com/erp/console-guide/journal-entry/#field-enabled" }}
                            style={{ width: 150, flexGrow: 0 }}
                        />
                    </NSRow>

                    <NSLine></NSLine>

                    <NSRepeater<JournalEntryLineItem, JournalEntryLineInput>
                        ref={this.Lines}
                        title="Lines" info={{ link: "https://namirasoft.com/erp/console-guide/journal-entry/#field-lines" }}
                        required
                        min={2}
                        hideSpaces
                        style={{ width: "100%", maxWidth: "100%" }}
                        createItem={(_gIndex, lIndex, ref, onChange, onDelete) => (
                            <JournalEntryLineItem
                                ref={ref}
                                servers={this.props.servers}
                                db={db}
                                companyId={companyId}
                                branchId={branchId}
                                multiCurrency={this.state.multiCurrency}
                                entryCurrencyId={this.state.entryCurrencyId}
                                onChange={() => { onChange(); this.recomputeTotals(); }}
                                onDelete={onDelete}
                                lIndex={lIndex}
                            />
                        )}
                        onItemChanged={() => this.recomputeTotals()}
                        onValuesChanged={() => this.recomputeTotals()}
                        getValue={(item, checkError) => item.getValue(checkError)}
                        setValue={(item, _index, value) => item.setValue(value)}
                        isEmpty={(item, _value) => item.isEmpty()}
                    />

                    {(() =>
                    {
                        let balanced = this.state.totalDebit === this.state.totalCredit;
                        let totalColor = balanced ? "hsla(234, 64%, 22%, 1)" : "red";
                        return (
                            <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "nowrap", width: "100%", color: "hsla(234, 64%, 22%, 1)", fontSize: 16, fontWeight: "bold" }}>
                                <div style={{ width: 28, flexShrink: 0 }} />
                                <div style={{ flexGrow: 1, flexShrink: 1, flexBasis: 0, minWidth: 0 }} />
                                <div style={{ width: NSBox.width.one, flexShrink: 0 }} />
                                <div style={{ width: NSBox.width.one, flexShrink: 0, textAlign: "right", paddingRight: 8 }}>Total:</div>
                                <div style={{ width: 160, flexShrink: 0, color: totalColor, paddingLeft: 8 }}>
                                    {this.state.totalDebit.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                </div>
                                <div style={{ width: 160, flexShrink: 0, color: totalColor, paddingLeft: 8 }}>
                                    {this.state.totalCredit.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                </div>
                                {this.state.multiCurrency && <div style={{ width: 120, flexShrink: 0 }} />}
                                <div style={{ width: 20, flexShrink: 0 }} />
                            </div>
                        );
                    })()}

                    <NSBoxTextArea ref={this.Description} required={false} title="Description" info={{ link: "https://namirasoft.com/erp/console-guide/journal-entry/#field-description" }} style={{ width: NSBox.width.quadruple }} />
                </NSColumn>
            </ErpLayout>
        );
    }
}